/**
 * The import hub on /app/twin/data-sources (Phase 5 of docs/plans/2026-09-22_cwc-platform-ingestion.md).
 *
 *   • Files sent for import: drop the plant's exports here (or let the plant agent send them). A drop or a pick waits
 *     in a "Ready to send" card, with an optional note for whoever reads the files (migration 0266: the reader author
 *     reads it as the uploader's words), until Send. Each file is stored once, its notes with it; a kind of file an
 *     approved reader recognises imports itself, new and changed days only.
 *     The table follows every job through probe, import and its end, and lets a writer cancel one that
 *     waits or reprocess one that failed (how a file held for review is released). A document family's file
 *     (the documents lane, migration 0228) goes to its product instead: the upload says where it was sent, a
 *     job whose file was sent reads so and offers no reprocess, and a job held until the org holds the
 *     product says what it waits for.
 *   • Readers: the extractors that turn a kind of file into data. A new kind of file waits for a reader to
 *     be written and dry-run on it; only a platform admin approves one, binding each of its streams to this
 *     org's equipment (the runner executes its code in the cloud), after reading what it will do
 *     (import-review.tsx), and saying for each stream that lands on equipment what its file is: the machine's own
 *     recorded log (history) or a sheet made for people (report), or as its reader says (one series per port, S5).
 *     Two streams that write into one data source but are set to different roles, and an approval the database could
 *     not take a lock for, are refused, and the sheet says so in words above its buttons. The defect words a draft
 *     proposes are saved with the approval, as the org's approved words; when they fail the approval stands, and the
 *     sheet says so. A platform admin may ask for a
 *     revision of the reader in use (#1428, import-revision-sheet.tsx): it stays in use until the revision's
 *     draft is approved, and approving that reads its files again.
 *   • Plant agents (plant-agents.tsx): the PCs running the Sahas Forge Agent; then the upload keys the older script
 *     agent (tools/ingest-agent) uploads with. Org admins only.
 *   • The analysis chip: where the org's analyses stand after the latest import.
 *
 * The consultant's conversation is not on this page (Arjun, 2026-10-06): it opens in the right-hand panel from the top
 * bar or the tab at the screen's edge, with this page as its context, like on any other page.
 *
 * Views are pure (rendered in tests without providers); the containers below them hold the hooks.
 */
import { useEffect, useId, useMemo, useState, type ReactNode } from "react";
import { useLocation } from "react-router-dom";
import { Ban, Copy, FileStack, KeyRound, Loader2, RefreshCw, RotateCcw, ScrollText, Upload, X } from "lucide-react";
import { Badge } from "@/lib/ui/badge";
import { Button } from "@/lib/ui/button";
import { Card } from "@/lib/ui/card";
import { Input } from "@/lib/ui/input";
import { EmptyState } from "@/lib/ui/empty-state";
import { ConfirmDialog } from "@/lib/ui/confirm-dialog";
import { FileDropzone } from "@/lib/ui/file-dropzone";
import { Sheet, SheetHeader } from "@/lib/ui/sheet";
import { useToast } from "@/lib/ui/toast";
import { useOrg } from "@/lib/org-context";
import { PlantAgentsPanel } from "@/routes/platform/plant-agents";
import { ApiClientError } from "@/lib/api-client";
import {
  useDataSourcesQuery,
  useEnabledPacksQuery,
  usePutVocabularyBindings,
  useAllTwinEntitiesQuery,
  useVocabularyBindingsQuery,
  useVocabularyTargetsQuery,
} from "@/lib/api";
import {
  IMPORT_NOTE_MAX,
  noteChars,
  useApproveImportVersion,
  useCancelImportJob,
  useCreateImportAgentKey,
  useImportAgentKeysQuery,
  useImportExtractorVersionQuery,
  useImportExtractorsQuery,
  useImportJobsQuery,
  useRejectImportVersion,
  useReprocessImportJob,
  useRevokeImportAgentKey,
  useUploadImportFile,
  type ImportAgentKey,
  type ImportAgentKeyCreated,
  type ImportExtractor,
  type ImportExtractorVersion,
  type ImportFileNote,
  type ImportJob,
} from "@/lib/api/imports";
import { usePlantTimeQuery } from "@/lib/api/plant-time";
import { useGradeChemistryQuery } from "@/lib/api/grade-chemistry";
import { useAnalysisJobsQuery, useRefreshAnalysis } from "@/lib/api/analysis-jobs";
import { ReviewFooterView, VersionReviewView } from "./import-review";
import { RevisionSheet } from "./import-revision-sheet";
import {
  READERS_SECTION_ID,
  analysisChip,
  approveBody,
  canCancelJob,
  deliveryWords,
  hashTarget,
  isPdfName,
  jobBadgeOf,
  jobBadgeText,
  jobKindText,
  jobRouted,
  jobStateText,
  missingBindings,
  offersReprocess,
  plantDateOf,
  replacedWords,
  reprocessMessage,
  reprocessRefusalText,
  scrollToSection,
  uploadMessage,
  type Bindings,
  type BindOption,
  type Chip,
  type ManifestStream,
} from "@/lib/imports/hub";
import { defectTargetsOf, proposedWordsOf, wordBindings, wordRows, type WordChoice } from "@/lib/imports/review";
import { chosenBindings, equipmentProposalsOf, startingBindings, type ItemsState } from "@/lib/imports/proposal";
import { previousVersionOf, sinceChanges, sinceHeader } from "@/lib/imports/since-draft";
import {
  answersBody,
  givenAnswers,
  reviewResponseOf,
  sendBackState,
  sentWith,
  type AnswerChoice,
} from "@/lib/imports/send-back";
import { runApprove, runRetryWords, wordsGate, type ReviewFlowIO, type ReviewPhase } from "@/lib/imports/review-flow";
import { isRevisionDraft, previousText, reapplyText, revisionApprovalText, revisionStateOf } from "@/lib/imports/revision";
import { JobFailureView } from "./import-diagnosis";
import { approveRefusalText, currentRolesOf, rolesBody, type RoleChoice } from "@/lib/imports/source-roles";

const MAX_FILE_MB = 64;
/** What the drop zone offers to pick; a drop is not filtered by it (the server checks every file). */
export const ACCEPT = ".csv,.tsv,.txt,.xls,.xlsx,.xlsm,.htm,.html,.xml,.json,.zip,.pdf";
const TH = "px-4 py-2 text-left font-semibold text-on-surface-muted";
/** The files list's own scroll box: about ten files tall, scrolling both ways. */
export const JOBS_SCROLL = "max-h-[32rem] overflow-auto";

function messageOf(err: unknown, fallback: string): string {
  if (err instanceof ApiClientError) {
    const body = err.body as { error?: string; detail?: string; message?: string } | null;
    return body?.detail || body?.message || body?.error || fallback;
  }
  return err instanceof Error && err.message ? err.message : fallback;
}

function fmtWhen(iso: string | null | undefined): string {
  if (!iso) return "";
  const d = new Date(iso);
  return Number.isNaN(d.getTime())
    ? iso
    : d.toLocaleString(undefined, { month: "short", day: "numeric", hour: "2-digit", minute: "2-digit" });
}

function fmtSize(bytes: number): string {
  if (bytes >= 1 << 20) return `${(bytes / (1 << 20)).toFixed(1)} MB`;
  if (bytes >= 1 << 10) return `${Math.round(bytes / (1 << 10))} KB`;
  return `${bytes} B`;
}

/** The textarea's look, as the revision sheet's note. */
const NOTE_CLS =
  "w-full rounded-md border border-border bg-surface px-2 py-1.5 text-xs text-on-surface focus:border-brand focus:outline-none disabled:opacity-60";

const ORIGIN_TEXT: Record<ImportJob["origin"], string> = {
  web: "web upload",
  agent: "plant agent",
  copilot: "copilot",
  backfill: "backfill",
};

// ── Views (pure) ───────────────────────────────────────────────────────────────

/** A file waiting in the "Ready to send" card: what the card shows of it. */
export type StagedFile = Pick<File, "name" | "size">;

/** The Send button's words: the count when more than one file goes. */
function sendLabel(count: number): string {
  return count > 1 ? `Send ${count} files` : "Send";
}

/**
 * The files dropped or picked, waiting to be sent: each with its size and removable, and one optional note that goes
 * with every file of the batch. Nothing is sent until Send; Clear drops the files and the note. A PDF is a process sheet
 * (P6): with onGrade, it gets a field for the grade it gives the limits of, offered from gradeOptions.
 */
export function StagedFilesView({
  files,
  note,
  busy,
  onNote,
  onRemove,
  onSend,
  onClear,
  grades,
  gradeOptions,
  onGrade,
}: {
  files: StagedFile[];
  note: string;
  busy: boolean;
  onNote: (note: string) => void;
  onRemove: (index: number) => void;
  onSend: () => void;
  onClear: () => void;
  /** Each file's grade, by its index: a PDF's alone is used. */
  grades?: string[];
  gradeOptions?: string[];
  onGrade?: (index: number, grade: string) => void;
}) {
  const hintId = useId();
  const gradesId = useId();
  const sheets = onGrade ? files.filter((f) => isPdfName(f.name)).length : 0;
  return (
    <Card className="flex flex-col gap-3 p-4" data-staged-files={files.length}>
      <div className="text-sm font-medium text-on-surface">Ready to send</div>
      <ul className="flex flex-col gap-1">
        {files.map((f, i) => (
          <li key={`${i}-${f.name}`} className="flex items-center gap-2 text-sm">
            <span className="min-w-0 flex-1 truncate text-on-surface" title={f.name}>
              {f.name}
            </span>
            <span className="text-[11px] text-on-surface-muted">{fmtSize(f.size)}</span>
            {onGrade && isPdfName(f.name) && (
              <Input
                aria-label={`Grade of ${f.name}`}
                className="h-7 w-36 text-xs"
                list={gradesId}
                placeholder="Its grade"
                value={grades?.[i] ?? ""}
                maxLength={100}
                disabled={busy}
                onChange={(e) => onGrade(i, e.target.value)}
              />
            )}
            <Button
              variant="ghost"
              size="icon"
              aria-label={`Remove ${f.name}`}
              title="Remove this file"
              disabled={busy}
              onClick={() => onRemove(i)}
            >
              <X className="size-4 text-on-surface-muted" />
            </Button>
          </li>
        ))}
      </ul>
      {sheets > 0 && (
        <p className="text-[11px] text-on-surface-muted" data-process-sheets={sheets}>
          A PDF is read as a process sheet: name the grade it gives the chemistry limits of. It is read for those
          limits, which you review under Process sheets on the Products and tooling page, and never imported as data.
        </p>
      )}
      {onGrade && (
        <datalist id={gradesId}>
          {(gradeOptions ?? []).map((g) => (
            <option key={g} value={g} />
          ))}
        </datalist>
      )}
      <label className="flex flex-col gap-1">
        <span className="text-xs font-medium text-on-surface">A note for whoever reads these files (optional)</span>
        <textarea
          className={`${NOTE_CLS} min-h-20`}
          value={note}
          maxLength={IMPORT_NOTE_MAX}
          disabled={busy}
          aria-describedby={hintId}
          onChange={(e) => onNote(e.target.value)}
        />
      </label>
      <div id={hintId} className="flex items-start justify-between gap-3 text-[11px] text-on-surface-muted">
        <span>Which machine they come from, what a column means, a unit, or a change at the plant.</span>
        <span className="shrink-0 tabular-nums" data-note-count={noteChars(note)}>
          {noteChars(note)}/{IMPORT_NOTE_MAX}
        </span>
      </div>
      <div className="flex gap-2">
        <Button size="sm" onClick={onSend} disabled={busy || files.length === 0}>
          {busy ? <Loader2 className="mr-1.5 size-3.5 animate-spin" /> : <Upload className="mr-1.5 size-3.5" />}
          {sendLabel(files.length)}
        </Button>
        <Button size="sm" variant="ghost" onClick={onClear} disabled={busy}>
          Clear
        </Button>
      </div>
    </Card>
  );
}

/**
 * A file's notes under its name in the files table: the newest in muted text, at most two lines, with every note on
 * hover, and listed newest first behind "2 notes" when there are more. Plain text, as the uploader wrote it.
 */
export function FileNotesView({ notes }: { notes?: ImportFileNote[] }) {
  if (!notes || notes.length === 0) return null;
  const newest = notes[notes.length - 1];
  return (
    <div className="mt-1 max-w-sm text-[11px] text-on-surface-muted" data-file-notes={notes.length}>
      <p className="line-clamp-2 whitespace-pre-line break-words" title={notes.map((n) => n.text).join("\n\n")}>
        {newest.text}
      </p>
      {notes.length > 1 && (
        <details className="mt-0.5">
          <summary className="cursor-pointer select-none">{notes.length} notes</summary>
          <ol className="mt-1 flex flex-col gap-1.5">
            {[...notes].reverse().map((n, i) => (
              <li key={`${i}-${n.created_at}`} className="whitespace-pre-line break-words">
                <div className="text-[10px]">{fmtWhen(n.created_at)}</div>
                {n.text}
              </li>
            ))}
          </ol>
        </details>
      )}
    </div>
  );
}

export function JobsTableView({
  jobs,
  canWrite,
  onCancel,
  onReprocess,
  busyId,
  isPlatformAdmin = false,
  plantZone,
}: {
  jobs: ImportJob[];
  canWrite: boolean;
  onCancel: (job: ImportJob) => void;
  onReprocess: (job: ImportJob) => void;
  busyId?: string | null;
  /** A platform admin also sees a failure's technical part (the raw error, the reader's own line). */
  isPlatformAdmin?: boolean;
  /** The plant's zone: a replaced job's date is the one the plant saw (the UTC date when unset). */
  plantZone?: string;
}) {
  // The list scrolls in its own box, its header held at the top, so a long history of files does not push the readers
  // and the rest of the page far down (Arjun 2026-10-23).
  return (
    <Card className="overflow-hidden p-0">
      <div className={JOBS_SCROLL}>
        <table className="w-full text-sm">
          <thead className="sticky top-0 z-10 border-b border-border bg-surface-elevated">
            <tr>
              <th className={TH}>File</th>
              <th className={TH}>Status</th>
              <th className={TH}>From</th>
              <th className={TH}>Received</th>
              <th className="px-4 py-2" />
            </tr>
          </thead>
          <tbody>
            {jobs.map((j) => (
              <tr key={j.id} className="border-b border-border align-top last:border-b-0 odd:bg-surface-elevated/30">
                <td className="px-4 py-2">
                  <div className="font-medium text-on-surface">{j.file.filename}</div>
                  <div className="text-[11px] text-on-surface-muted">{fmtSize(j.file.byte_size)}</div>
                  <FileNotesView notes={j.file.notes} />
                </td>
                <td className="px-4 py-2">
                  <Badge variant={jobBadgeOf(j)} className="text-[10px] uppercase">
                    {jobBadgeText(j)}
                  </Badge>
                  {/* A failed or cancelled job another job of its file stands in place of says so, not "failed". */}
                  <div className="mt-1 text-xs text-on-surface-muted" data-replaced-by={j.replaced_by?.id}>
                    {replacedWords(j, (iso) => plantDateOf(iso, plantZone)) || jobStateText(j)}
                  </div>
                  {jobKindText(j) && <div className="mt-0.5 text-[11px] text-on-surface-muted">{jobKindText(j)}</div>}
                  {/* A sent file's error is the code it was cancelled with, not a failure. */}
                  {j.error && !jobRouted(j) && <JobFailureView job={j} isPlatformAdmin={isPlatformAdmin} />}
                </td>
                <td className="px-4 py-2 text-xs text-on-surface-muted">
                  {ORIGIN_TEXT[j.origin] ?? j.origin}
                  {/* A plant agent's file: sent from the PC itself, or through a gateway (named). */}
                  {deliveryWords(j) && (
                    <div className="text-[11px]" data-delivery={j.delivered_via}>
                      {deliveryWords(j)}
                    </div>
                  )}
                </td>
                <td className="px-4 py-2 text-xs text-on-surface-muted">{fmtWhen(j.created_at)}</td>
                <td className="whitespace-nowrap px-4 py-2 text-right">
                  {busyId === j.id && <Loader2 className="inline size-4 animate-spin text-on-surface-muted" />}
                  {canWrite && canCancelJob(j.status) && (
                    <Button
                      variant="ghost"
                      size="icon"
                      aria-label={`Cancel ${j.file.filename}`}
                      title="Cancel: the file stays stored and can be reprocessed"
                      onClick={() => onCancel(j)}
                    >
                      <Ban className="size-4 text-on-surface-muted" />
                    </Button>
                  )}
                  {canWrite && offersReprocess(j) && (
                    <Button
                      variant="ghost"
                      size="icon"
                      aria-label={`Reprocess ${j.file.filename}`}
                      title="Reprocess: read the file again from the start"
                      onClick={() => onReprocess(j)}
                    >
                      <RotateCcw className="size-4 text-brand" />
                    </Button>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </Card>
  );
}

export function ExtractorsView({
  extractors,
  isPlatformAdmin,
  onReview,
  onRevise,
}: {
  extractors: ImportExtractor[];
  isPlatformAdmin: boolean;
  onReview: (extractor: ImportExtractor, version: ImportExtractorVersion) => void;
  /** Ask for a revision of the version in use (#1428); offered to platform admins only. */
  onRevise?: (extractor: ImportExtractor, version: ImportExtractorVersion) => void;
}) {
  return (
    <div className="flex flex-col gap-2">
      {extractors.map((e) => {
        const versions = e.versions ?? [];
        const active = versions.find((v) => v.status === "active");
        const drafts = versions.filter((v) => v.status === "draft");
        const lastRejected = versions.find((v) => v.status === "rejected");
        const revision = revisionStateOf(versions);
        return (
          <Card key={e.id} className="flex flex-col gap-1.5 p-4">
            <div className="flex flex-wrap items-baseline gap-2">
              <span className="font-medium text-on-surface">{e.label || e.key}</span>
              <span className="font-mono text-[11px] text-on-surface-muted">{e.key}</span>
              {active ? (
                <Badge variant="success" className="text-[10px]">
                  v{active.version} in use
                </Badge>
              ) : (
                <Badge variant="secondary" className="text-[10px]">
                  none in use yet
                </Badge>
              )}
              {revision.kind === "can_ask" && isPlatformAdmin && onRevise && (
                <Button
                  size="sm"
                  variant="ghost"
                  className="ml-auto"
                  aria-label={`Ask for a revision of ${e.label || e.key} v${revision.active.version}`}
                  onClick={() => onRevise(e, revision.active)}
                >
                  Ask for a revision
                </Button>
              )}
            </div>
            {revision.kind === "asked" && (
              <div className="flex flex-col gap-0.5 text-xs text-on-surface-muted">
                <span className="line-clamp-2" title={revision.note}>
                  A revision of v{revision.active.version} was asked: {revision.note}
                </span>
                <span>Its draft shows here for review once the author has written it; its job is under Files.</span>
              </div>
            )}
            {drafts.map((d) => (
              <div key={d.id} className="flex flex-wrap items-center gap-2 text-xs text-on-surface-muted">
                <Badge variant="warning" className="text-[10px]">
                  v{d.version} waits for review
                </Badge>
                {isPlatformAdmin ? (
                  <Button
                    size="sm"
                    variant="outline"
                    aria-label={`Review ${e.label || e.key} v${d.version}`}
                    onClick={() => onReview(e, d)}
                  >
                    <ScrollText className="mr-1.5 size-3.5" />
                    Review
                  </Button>
                ) : (
                  <span>a platform admin reviews it before it reads any file</span>
                )}
              </div>
            ))}
            {lastRejected?.review_note && drafts.length === 0 && (
              <div className="text-xs text-on-surface-muted">
                v{lastRejected.version} was sent back: {lastRejected.review_note}
              </div>
            )}
          </Card>
        );
      })}
    </div>
  );
}

function keyState(k: ImportAgentKey): { text: string; live: boolean } {
  if (k.revoked_at) return { text: `revoked ${fmtWhen(k.revoked_at)}`, live: false };
  if (k.expires_at && new Date(k.expires_at).getTime() <= Date.now()) {
    return { text: `expired ${fmtWhen(k.expires_at)}`, live: false };
  }
  return { text: k.expires_at ? `works until ${fmtWhen(k.expires_at)}` : "works until revoked", live: true };
}

export function AgentKeysView({ keys, onRevoke }: { keys: ImportAgentKey[]; onRevoke: (k: ImportAgentKey) => void }) {
  return (
    <Card className="overflow-hidden p-0">
      <div className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="border-b border-border">
            <tr>
              <th className={TH}>Plant PC</th>
              <th className={TH}>Key</th>
              <th className={TH}>Last used</th>
              <th className={TH}>State</th>
              <th className="px-4 py-2" />
            </tr>
          </thead>
          <tbody>
            {keys.map((k) => {
              const st = keyState(k);
              return (
                <tr key={k.id} className="border-b border-border last:border-b-0 odd:bg-surface-elevated/30">
                  <td className="px-4 py-2 font-medium text-on-surface">{k.label}</td>
                  <td className="px-4 py-2 font-mono text-xs text-on-surface-muted">{k.prefix}…</td>
                  <td className="px-4 py-2 text-xs text-on-surface-muted">
                    {k.last_used_at ? fmtWhen(k.last_used_at) : "never used"}
                  </td>
                  <td className="px-4 py-2 text-xs text-on-surface-muted">{st.text}</td>
                  <td className="px-4 py-2 text-right">
                    {st.live && (
                      <Button
                        variant="ghost"
                        size="sm"
                        aria-label={`Revoke ${k.label}`}
                        onClick={() => onRevoke(k)}
                      >
                        Revoke
                      </Button>
                    )}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </Card>
  );
}

export function NewKeyNotice({
  created,
  onDone,
  apiUrl,
  onCopy,
}: {
  created: ImportAgentKeyCreated;
  onDone: () => void;
  apiUrl?: string;
  onCopy?: (text: string) => void;
}) {
  const url = apiUrl || "https://<your Foundry Twin address>";
  return (
    <Card data-copilot-private="" className="flex flex-col gap-3 border-warning/60 p-4">
      <div className="text-sm font-medium text-on-surface">
        The key for {created.label}. This is the only time it is shown: copy it to the plant PC now.
      </div>
      <div className="flex items-center gap-2">
        <code className="min-w-0 flex-1 break-all rounded-md bg-surface-muted px-2 py-1.5 font-mono text-xs text-on-surface">
          {created.key}
        </code>
        {onCopy && (
          <Button size="sm" variant="outline" onClick={() => onCopy(created.key)} aria-label="Copy the key">
            <Copy className="mr-1.5 size-3.5" />
            Copy
          </Button>
        )}
      </div>
      <div className="text-xs text-on-surface-muted">
        On the plant PC, save the key alone in a file only the agent&apos;s account can read (for example
        C:\ProgramData\SahasForge\agent.key), then schedule the agent (tools/ingest-agent) every 15 minutes:
      </div>
      <code className="break-all rounded-md bg-surface-muted px-2 py-1.5 font-mono text-[11px] text-on-surface">
        python -m ingest_agent --api {url} --folder &quot;D:\Exports&quot; --key-file
        C:\ProgramData\SahasForge\agent.key --once
      </code>
      <Button size="sm" className="w-fit" onClick={onDone}>
        I have copied it
      </Button>
    </Card>
  );
}

export function AnalysisChipView({
  chip,
  canRefresh,
  onRefresh,
  refreshing,
}: {
  chip: Chip;
  canRefresh: boolean;
  onRefresh: () => void;
  refreshing: boolean;
}) {
  return (
    <div className="flex items-center gap-1.5">
      <Badge variant={chip.tone} className="max-w-xs truncate text-[11px]" title={chip.detail ?? chip.text}>
        {chip.text}
      </Badge>
      {canRefresh && (
        <Button size="sm" variant="ghost" onClick={onRefresh} disabled={refreshing} aria-label="Refresh the analysis">
          {refreshing ? <Loader2 className="mr-1 size-3.5 animate-spin" /> : <RefreshCw className="mr-1 size-3.5" />}
          Refresh
        </Button>
      )}
    </div>
  );
}

// ── Containers ───────────────────────────────────────────────────────────────────

/** The org's analysis status, for the page header. Renders nothing until there is something to say. */
export function AnalysisStatusChip() {
  const { canWrite } = useOrg();
  const query = useAnalysisJobsQuery();
  const refresh = useRefreshAnalysis();
  const toast = useToast();
  if (!query.data) return null;
  const onRefresh = async () => {
    try {
      const res = await refresh.mutateAsync();
      toast.push("ok", res.merged ? "Joined the refresh already queued." : "Analysis refresh queued.");
    } catch (err) {
      toast.push("error", messageOf(err, "Couldn't queue the refresh."));
    }
  };
  return (
    <AnalysisChipView
      chip={analysisChip(query.data.jobs ?? [])}
      canRefresh={canWrite}
      onRefresh={onRefresh}
      refreshing={refresh.isPending}
    />
  );
}

/** The files sent for import and the readers, first on the page; `afterReaders` is what the page shows next, before
 *  the plant agents (the Data sources page puts its coverage panel there). */
export function ImportHub({ afterReaders }: { afterReaders?: ReactNode }) {
  const { canWrite, canAdmin, isPlatformAdmin } = useOrg();
  return (
    <>
      <FilesPanel canWrite={canWrite} isPlatformAdmin={isPlatformAdmin} />
      <ReadersPanel isPlatformAdmin={isPlatformAdmin} />
      {afterReaders}
      {canAdmin && (
        <>
          <PlantAgentsPanel />
          <AgentKeysPanel />
        </>
      )}
    </>
  );
}

function FilesPanel({ canWrite, isPlatformAdmin }: { canWrite: boolean; isPlatformAdmin: boolean }) {
  const query = useImportJobsQuery();
  // The plant's zone, so a replaced job's date is the one the plant saw (the same cached read every plant-time page shares).
  const plantTime = usePlantTimeQuery();
  const upload = useUploadImportFile();
  const cancel = useCancelImportJob();
  const reprocess = useReprocessImportJob();
  const toast = useToast();
  const [busyId, setBusyId] = useState<string | null>(null);
  const [sending, setSending] = useState(0);
  // The files dropped or picked, waiting in the Ready to send card, and the note that goes with every one of them.
  const [staged, setStaged] = useState<File[]>([]);
  const [note, setNote] = useState("");
  // The grade each staged PDF is a process sheet for (P6), by the file; the grades are offered once a PDF is staged.
  const [sheetGrades, setSheetGrades] = useState<Map<File, string>>(() => new Map());
  const hasPdf = staged.some((f) => isPdfName(f.name));
  const gradeDoc = useGradeChemistryQuery(hasPdf);
  const gradeOptions = useMemo(() => (gradeDoc.data?.grades ?? []).map((g) => g.grade), [gradeDoc.data]);
  const jobs = query.data?.items ?? [];

  // A drop or a pick waits for Send. The size and empty checks run as each file is added; the same file twice is
  // listed once.
  const onFiles = (files: File[]) => {
    const fit: File[] = [];
    for (const f of files) {
      if (f.size > MAX_FILE_MB * 1024 * 1024) {
        toast.push("error", `${f.name} is over ${MAX_FILE_MB} MB: split the export and send the parts.`);
      } else if (f.size === 0) {
        toast.push("error", `${f.name} is empty.`);
      } else {
        fit.push(f);
      }
    }
    const same = (a: File, b: File) => a.name === b.name && a.size === b.size && a.lastModified === b.lastModified;
    if (fit.length > 0) setStaged((s) => [...s, ...fit.filter((f) => !s.some((x) => same(x, f)))]);
  };

  // Every file goes with the same note. A file whose upload failed stays in the card, with the note, to send again.
  const onSend = async () => {
    const batch = staged;
    const left: File[] = [];
    setSending(batch.length);
    for (const f of batch) {
      try {
        // The file's job, or the document it became when the documents lane sent it to a product.
        const grade = isPdfName(f.name) ? sheetGrades.get(f) : undefined;
        const answer = await upload.mutateAsync({ file: f, note, grade });
        toast.push("ok", uploadMessage(answer, grade));
      } catch (err) {
        toast.push("error", `${f.name}: ${messageOf(err, "the upload failed")}`);
        left.push(f);
      }
      setSending((n) => n - 1);
    }
    setStaged(left);
    setSheetGrades((m) => new Map([...m].filter(([f]) => left.includes(f))));
    if (left.length === 0) setNote("");
  };

  // ok and bad say what came of it from the answer (a reprocess may send a held file to its product) or the refusal.
  const act = async (
    j: ImportJob,
    fn: (id: string) => Promise<ImportJob>,
    ok: (job: ImportJob) => string,
    bad: (err: unknown) => string,
  ) => {
    setBusyId(j.id);
    try {
      const job = await fn(j.id);
      toast.push("ok", `${j.file.filename}: ${ok(job)}`);
    } catch (err) {
      toast.push("error", `${j.file.filename}: ${bad(err)}`);
    } finally {
      setBusyId(null);
    }
  };

  return (
    <section className="flex flex-col gap-3">
      <div className="flex items-center gap-2">
        <FileStack className="size-4 text-on-surface-muted" />
        <h2 className="text-sm font-semibold text-on-surface">Files sent for import</h2>
        <span className="text-xs text-on-surface-muted">
          each kind of file is read by an approved reader and imports itself, new and changed days only
        </span>
      </div>
      {canWrite && (
        <FileDropzone
          multiple
          onFiles={onFiles}
          accept={ACCEPT}
          acceptLabel="XLSX · XLS · CSV · HTML · ZIP · PDF"
          maxSizeMb={MAX_FILE_MB}
          disabled={sending > 0}
          icon={sending > 0 ? Loader2 : Upload}
          hint={sending > 0 ? `Sending ${sending} file(s)…` : "Drop the plant's exports here, or click to choose"}
        />
      )}
      {canWrite && staged.length > 0 && (
        <StagedFilesView
          files={staged}
          note={note}
          busy={sending > 0}
          onNote={setNote}
          onRemove={(i) => {
            const gone = staged[i];
            setStaged((s) => s.filter((_, k) => k !== i));
            setSheetGrades((m) => new Map([...m].filter(([f]) => f !== gone)));
          }}
          onSend={() => void onSend()}
          onClear={() => {
            setStaged([]);
            setNote("");
            setSheetGrades(new Map());
          }}
          grades={staged.map((f) => sheetGrades.get(f) ?? "")}
          gradeOptions={gradeOptions}
          onGrade={(i, g) => {
            const f = staged[i];
            if (f) setSheetGrades((m) => new Map(m).set(f, g));
          }}
        />
      )}
      {query.isLoading ? (
        <p className="text-sm text-on-surface-muted">Loading import jobs…</p>
      ) : query.isError ? (
        <EmptyState icon={FileStack} title="Couldn't load the import jobs" description={messageOf(query.error, "")} />
      ) : jobs.length === 0 ? (
        <EmptyState
          icon={FileStack}
          title="No files sent here yet"
          description="Drop an export above, or set up a plant agent to send them. The first file of a new kind waits for a reader to be written and approved; after that the same kind of file imports itself. Data imported another way (mapped by hand, or a historical import) is listed under Recent imports below."
        />
      ) : (
        <JobsTableView
          jobs={jobs}
          canWrite={canWrite}
          isPlatformAdmin={isPlatformAdmin}
          plantZone={plantTime.data?.timezone}
          busyId={busyId}
          onCancel={(j) =>
            void act(j, cancel.mutateAsync, () => "cancelled", (err) => messageOf(err, "couldn't cancel"))
          }
          onReprocess={(j) =>
            void act(
              j,
              reprocess.mutateAsync,
              reprocessMessage,
              (err) => reprocessRefusalText(err) ?? messageOf(err, "couldn't reprocess"),
            )
          }
        />
      )}
    </section>
  );
}

function ReadersPanel({ isPlatformAdmin }: { isPlatformAdmin: boolean }) {
  const query = useImportExtractorsQuery();
  const [review, setReview] = useState<{ extractor: ImportExtractor; version: ImportExtractorVersion } | null>(null);
  const [revise, setRevise] = useState<{ extractor: ImportExtractor; version: ImportExtractorVersion } | null>(null);
  const extractors = query.data?.items ?? [];
  const { hash } = useLocation();
  const listed = !query.isLoading;
  // The hub opened with #import-readers in its address (from "Review and approve", or a reload after it): the router
  // does not scroll to a hash, so the panel brings itself into view once its readers are listed (#843 review N4).
  useEffect(() => {
    if (listed && hashTarget(hash) === READERS_SECTION_ID) scrollToSection(document, READERS_SECTION_ID);
  }, [hash, listed]);
  return (
    <section id={READERS_SECTION_ID} className="flex flex-col gap-3">
      <div className="flex items-center gap-2">
        <ScrollText className="size-4 text-on-surface-muted" />
        <h2 className="text-sm font-semibold text-on-surface">Readers</h2>
        <span className="text-xs text-on-surface-muted">how each kind of file becomes data</span>
      </div>
      {query.isLoading ? (
        <p className="text-sm text-on-surface-muted">Loading readers…</p>
      ) : extractors.length === 0 ? (
        <EmptyState
          icon={ScrollText}
          title="No readers yet"
          description="A reader is written for the first file of each kind, checked on that file, and approved before it reads anything."
        />
      ) : (
        <ExtractorsView
          extractors={extractors}
          isPlatformAdmin={isPlatformAdmin}
          onReview={(extractor, version) => setReview({ extractor, version })}
          onRevise={isPlatformAdmin ? (extractor, version) => setRevise({ extractor, version }) : undefined}
        />
      )}
      {review && (
        <ReviewSheet extractor={review.extractor} version={review.version} onClose={() => setReview(null)} />
      )}
      {revise && (
        <RevisionSheet extractor={revise.extractor} version={revise.version} onClose={() => setRevise(null)} />
      )}
    </section>
  );
}

function ReviewSheet({
  extractor,
  version,
  onClose,
}: {
  extractor: ImportExtractor;
  version: ImportExtractorVersion;
  onClose: () => void;
}) {
  const detail = useImportExtractorVersionQuery(extractor.id, version.version);
  // The version before this one, for what changed since (and the note it was sent back with).
  const before = previousVersionOf(extractor.versions ?? [], version);
  const beforeDetail = useImportExtractorVersionQuery(before ? extractor.id : null, before?.version ?? null);
  const entitiesQuery = useAllTwinEntitiesQuery(); // every item, paged: a binding is never judged against a cut list
  const targetsQuery = useVocabularyTargetsQuery();
  const bindingsQuery = useVocabularyBindingsQuery();
  const packsQuery = useEnabledPacksQuery();
  const approve = useApproveImportVersion();
  const reject = useRejectImportVersion();
  const jobsQuery = useImportJobsQuery();
  const putWords = usePutVocabularyBindings();
  const { can } = useOrg();
  const toast = useToast();
  // The reviewer's choices, over the bindings the extractor's streams already have (a new version of a
  // reader in use keeps its equipment unless the reviewer changes it).
  const [picked, setPicked] = useState<Bindings>({});
  // The reviewer's meaning for each defect word the draft proposes, over where each word starts.
  const [wordPicks, setWordPicks] = useState<Record<string, WordChoice>>({});
  const [note, setNote] = useState("");
  // The reviewer's role for each stream's data source (one series per port, S5): a stream not chosen for is left as its
  // reader says, and the approval sends nothing for it.
  const [roles, setRoles] = useState<Record<string, RoleChoice>>({});
  // A refused approval the reviewer can act on here: two streams set to different roles of one data source, or a lock
  // conflict to send again. Said above the buttons until the next approval or a role changes.
  const [refusal, setRefusal] = useState<string | null>(null);
  const dataSourcesQuery = useDataSourcesQuery();
  // The reviewer's answers to the draft's four-part questions, by their place (plan v21 B5): they go with a send-back.
  const [answers, setAnswers] = useState<Record<number, AnswerChoice>>({});
  const sendBack = sendBackState(note, answers);
  // Where the sheet stands (lib/imports/review-flow): once the version is approved and its words were not saved, the
  // approval stands, and the sheet stays open only to say so and to save the words again.
  const [phase, setPhase] = useState<ReviewPhase>({ kind: "idle" });
  // Every item of the plant, of any kind: a lab's tests land on an instrument, a store's levels on a station.
  const entities = useMemo<BindOption[]>(
    () =>
      (entitiesQuery.data?.items ?? [])
        .map((e) => ({ id: e.id, name: e.name, kind: e.kind, templateId: e.template_id }))
        .sort((a, b) => a.name.localeCompare(b.name)),
    [entitiesQuery.data],
  );

  const v = detail.data;
  const streams = useMemo(() => (v?.manifest?.streams ?? []) as ManifestStream[], [v?.manifest?.streams]);
  // Each picker starts from the binding the streams already have, else the item the author proposed, else the item
  // its name clearly points to (lib/imports/proposal prefillBindings); the reviewer's choice is over all of them.
  const earlier = useMemo(() => {
    const out: Bindings = {};
    for (const s of detail.data?.streams ?? []) out[s.key] = { ...(s.bindings ?? {}) };
    return out;
  }, [detail.data?.streams]);
  const proposals = useMemo(() => equipmentProposalsOf(v?.author_notes), [v?.author_notes]);
  // The role each stream's data source holds now, for a stream that already writes through one.
  // Found by the manifest's own key, as the sheet reads it, and by the summary's trimmed one (currentRolesOf).
  const currentRoles = useMemo(
    () => currentRolesOf(detail.data?.streams ?? [], streams, dataSourcesQuery.data?.data_sources ?? []),
    [dataSourcesQuery.data, detail.data?.streams, streams],
  );
  // Nothing is prefilled, and no picker says anything, until every item of the plant is loaded (lib/imports/proposal
  // startingBindings): an empty list while they load, or when they failed, would read as every item gone.
  const items: ItemsState = entitiesQuery.isSuccess ? "ready" : entitiesQuery.isError ? "failed" : "loading";
  const prefill = useMemo(
    () => startingBindings(items, streams, earlier, proposals, entities),
    [items, streams, earlier, proposals, entities],
  );
  const chosen = useMemo(() => chosenBindings(prefill.bindings, picked), [prefill, picked]);
  // The version before: its head from the extractor's list at once; what changed once it is loaded, with the items'
  // names once they are (without them, an item reads as not in the list).
  const since = useMemo(() => {
    if (!before) return null;
    const changes =
      v && beforeDetail.data && items !== "loading"
        ? sinceChanges(v, beforeDetail.data, Object.fromEntries(entities.map((e) => [e.id, e.name])))
        : null;
    return {
      header: sinceHeader(before),
      changes,
      failed: beforeDetail.isError,
      given: givenAnswers(beforeDetail.data),
      response: reviewResponseOf(v?.author_notes),
    };
  }, [before, v, beforeDetail.data, beforeDetail.isError, items, entities]);

  const missing = v ? missingBindings(streams, chosen) : ["loading"];
  const label = extractor.label || extractor.key;
  // A draft written for a revision of the version in use (#1428) replaces it, and its approval reads its files again.
  const inUse = (extractor.versions ?? []).find((x) => x.status === "active");
  const revisionOf = inUse && v && isRevisionDraft(v, jobsQuery.data?.items ?? []) ? inUse.version : null;

  // The words the view lists, from the same inputs, so what is saved is what the reviewer sees.
  const targets = targetsQuery.data?.defects ?? null;
  const orgBindings = bindingsQuery.data?.items;
  const rows = useMemo(
    () => wordRows(proposedWordsOf(v?.author_notes), defectTargetsOf(targets), orgBindings),
    [v?.author_notes, targets, orgBindings],
  );
  const words = v ? wordBindings(rows, wordPicks, v) : [];
  // Whether the words can go with the approval: approve waits for the org's decided words, and none is sent when they
  // did not load, or when the role or the org's packs mean the server would refuse it.
  const gate = wordsGate({
    proposed: rows.length,
    bindings: bindingsQuery.isSuccess ? "ready" : bindingsQuery.isError ? "failed" : "loading",
    canWrite: can("model.write"),
    foundry: packsQuery.isSuccess
      ? (packsQuery.data?.packs ?? []).some((p) => p.pack === "foundry")
        ? "yes"
        : "no"
      : packsQuery.isError
        ? "unknown"
        : "loading",
  });

  // The flow's side of the sheet: its mutations, its toast and its close (the order of the calls is review-flow's).
  const io: ReviewFlowIO = {
    approve: () => {
      setRefusal(null);
      return v
        ? approve
            .mutateAsync({
              extractorId: extractor.id,
              version: v.version,
              body: approveBody(streams, chosen, rolesBody(roles)),
            })
            .then((res) => {
              const more = reapplyText(res.reapply);
              if (more) toast.push("ok", `${label} v${v.version}: ${more}`);
              return res;
            })
        : Promise.reject(new Error("the version is not loaded"));
    },
    putWords: (bindings) => putWords.mutateAsync(bindings),
    setPhase,
    toast: (tone, text) => toast.push(tone, text),
    close: onClose,
    approveError: (err) => {
      const words = approveRefusalText(err);
      if (words) setRefusal(words);
      return words ?? messageOf(err, "Couldn't approve this version.");
    },
  };

  const onReject = async () => {
    if (!v || !sendBack.ok) return;
    setPhase({ kind: "sending_back" });
    try {
      await reject.mutateAsync({
        extractorId: extractor.id,
        version: v.version,
        note: note.trim(),
        answers: answersBody(answers),
      });
      toast.push("ok", `${label} v${v.version} sent back to its author with ${sentWith(note, answers)}.`);
      onClose();
    } catch (err) {
      setPhase({ kind: "idle" });
      toast.push("error", messageOf(err, "Couldn't send this version back."));
    }
  };

  return (
    <Sheet open onClose={onClose} widthClass="max-w-2xl" ariaLabel={`Review ${label} v${version.version}`}>
      <SheetHeader title={`Review ${label} v${version.version}`} subtitle={extractor.key} onClose={onClose} />
      <div className="flex-1 overflow-y-auto">
        {detail.isLoading || !v ? (
          <p className="p-5 text-sm text-on-surface-muted">Loading the version…</p>
        ) : (
          <VersionReviewView
            version={v}
            entities={entities}
            chosen={chosen}
            sources={prefill.source}
            picked={picked}
            since={since}
            items={items}
            answering={{
              choices: answers,
              onChoose: (i, c) =>
                setAnswers((cur) => {
                  const next = { ...cur };
                  if (c === null) delete next[i];
                  else next[i] = c;
                  return next;
                }),
              locked: phase.kind !== "idle",
            }}
            onBind={(sk, eq, id) => setPicked((c) => ({ ...c, [sk]: { ...(c[sk] ?? {}), [eq]: id } }))}
            roles={{
              choices: roles,
              current: currentRoles,
              onChoose: (key, choice) => {
                setRefusal(null);
                setRoles((c) => ({ ...c, [key]: choice }));
              },
            }}
            locked={phase.kind === "words_failed" || phase.kind === "retrying"}
            words={{
              targets,
              bindings: orgBindings,
              choices: wordPicks,
              onChoose: (key, choice) => setWordPicks((c) => ({ ...c, [key]: choice })),
              hint: gate.notice || undefined,
            }}
          />
        )}
      </div>
      <div className="flex flex-col gap-2 border-t border-border p-4">
        {revisionOf !== null && v && (
          <p className="text-xs text-on-surface-muted">{revisionApprovalText(revisionOf, v.version)}</p>
        )}
        {revisionOf !== null && v && previousText(v.validation) && (
          <p className="text-xs text-on-surface-muted" data-previous-file="">
            {previousText(v.validation)}
          </p>
        )}
        <ReviewFooterView
          phase={phase}
          missing={missing.length}
          waiting={gate.waiting}
          note={note}
          sendBack={sendBack}
          onNote={setNote}
          onApprove={() => {
            if (v) void runApprove(io, { label, version: v.version, words, blocked: gate.blocked });
          }}
          onReject={() => void onReject()}
          onRetry={() => {
            if (v && (phase.kind === "words_failed" || phase.kind === "retrying")) {
              void runRetryWords(io, { label, version: v.version, words }, phase.message);
            }
          }}
          onClose={onClose}
          refusal={refusal}
        />
      </div>
    </Sheet>
  );
}

export function AgentKeysPanel() {
  const query = useImportAgentKeysQuery(true);
  const create = useCreateImportAgentKey();
  const revoke = useRevokeImportAgentKey();
  const toast = useToast();
  const [label, setLabel] = useState("");
  const [expires, setExpires] = useState("");
  const [created, setCreated] = useState<ImportAgentKeyCreated | null>(null);
  const [pendingRevoke, setPendingRevoke] = useState<ImportAgentKey | null>(null);
  const keys = query.data?.items ?? [];

  const onCreate = async () => {
    const body: { label: string; expires_at?: string } = { label: label.trim() };
    if (expires) body.expires_at = new Date(`${expires}T23:59:59`).toISOString();
    try {
      const res = await create.mutateAsync(body);
      setCreated(res);
      setLabel("");
      setExpires("");
    } catch (err) {
      toast.push("error", messageOf(err, "Couldn't create the key."));
    }
  };

  const onCopy = async (text: string) => {
    try {
      await navigator.clipboard.writeText(text);
      toast.push("ok", "Key copied.");
    } catch {
      toast.push("warn", "Couldn't copy: select the key and copy it by hand.");
    }
  };

  const confirmRevoke = async () => {
    if (!pendingRevoke) return;
    try {
      await revoke.mutateAsync(pendingRevoke.id);
      toast.push("ok", `${pendingRevoke.label}'s key no longer works.`);
    } catch (err) {
      toast.push("error", messageOf(err, "Couldn't revoke the key."));
    } finally {
      setPendingRevoke(null);
    }
  };

  return (
    <section className="flex flex-col gap-3">
      <div className="flex items-center gap-2">
        <KeyRound className="size-4 text-on-surface-muted" />
        <h2 className="text-sm font-semibold text-on-surface">Upload keys</h2>
        <span className="text-xs text-on-surface-muted">
          for the older script agent: a plant PC uploads its exports with a key of this org
        </span>
      </div>
      {created ? (
        <NewKeyNotice
          created={created}
          onDone={() => setCreated(null)}
          apiUrl={typeof window !== "undefined" ? window.location.origin : undefined}
          onCopy={(t) => void onCopy(t)}
        />
      ) : (
        <Card className="flex flex-wrap items-end gap-2 p-4">
          <div className="flex min-w-48 flex-1 flex-col gap-1 text-xs text-on-surface-muted">
            <label htmlFor="agent-key-label">The plant PC&apos;s name</label>
            <Input
              id="agent-key-label"
              value={label}
              onChange={(e) => setLabel(e.target.value)}
              placeholder="Melt shop PC"
              maxLength={200}
            />
          </div>
          <div className="flex flex-col gap-1 text-xs text-on-surface-muted">
            <label htmlFor="agent-key-expires">Stops working on (optional)</label>
            <Input id="agent-key-expires" type="date" value={expires} onChange={(e) => setExpires(e.target.value)} />
          </div>
          <Button size="sm" disabled={!label.trim() || create.isPending} onClick={() => void onCreate()}>
            <KeyRound className="mr-1.5 size-3.5" />
            Create a key
          </Button>
        </Card>
      )}
      {query.isError ? (
        <p className="text-sm text-danger">{messageOf(query.error, "Couldn't load the upload keys.")}</p>
      ) : (
        query.isSuccess &&
        (keys.length > 0 ? (
          <AgentKeysView keys={keys} onRevoke={setPendingRevoke} />
        ) : (
          <p className="text-sm text-on-surface-muted">No upload keys.</p>
        ))
      )}
      <ConfirmDialog
        open={pendingRevoke !== null}
        onClose={() => setPendingRevoke(null)}
        onConfirm={confirmRevoke}
        title="Revoke this key?"
        description={
          pendingRevoke
            ? `${pendingRevoke.label}'s agent stops sending files at once. Files it already sent stay.`
            : ""
        }
        confirmLabel="Revoke"
        destructive
      />
    </section>
  );
}
